import { CURRENCY_CHOICES } from "../../constants/tenderChoices";
import { formatGroupedNumber } from "../../utils/currencyFormatter";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import CustomDropdown from "../common/CustomDropdown";

// fieldErrors (optional): inline validation errors keyed by error group —
// this step renders the "salary" group (T004).
const TenderFormDetails = ({
  formData,
  setFormData,
  choices,
  fieldErrors = {},
}) => {
  const { tenderTypes, staffTenderTypes, salaryTypes, recruitingForChoices } =
    choices;

  // Input mask for the fee / salary fields. Groups with the platform's
  // apostrophe (Marin, 2026-08-02) so what the club types matches what the
  // request card prints back afterwards.
  //
  // The strip accepts BOTH separators: a comma from a value this field wrote
  // before the change, and an apostrophe from one it writes now. Dropping the
  // comma case would make an already-filled request unreadable on reopen.
  const formatAmount = (value) => {
    if (!value) return "";
    return formatGroupedNumber(String(value).replace(/[,']/g, ""), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleAmountChange = (e, field, maxDigits = 8) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    // Limit to maxDigits maximum to comply with API validation
    if (value.length > maxDigits) {
      value = value.substring(0, maxDigits);
    }
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="bg-[#F8F9FB] rounded-lg p-8">
      <h3 className="text-xs font-medium uppercase text-secondary mb-6">
        REQUEST DETAILS
      </h3>

      <div className="space-y-6">
        {/* Currency Selection - Global */}
        <div>
          <CustomDropdown
            label="Currency"
            options={CURRENCY_CHOICES}
            value={formData.currency}
            onChange={(value) => setFormData({ ...formData, currency: value })}
            placeholder="Select currency"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search currency..."
          />
        </div>

        <div
          className={`grid ${
            formData.tenderFor === "player" ? "lg:grid-cols-3 sm:grid-cols-2  " : "sm:grid-cols-2"
          }  gap-6`}
        >
          <div>
            <CustomDropdown
              label="Request for"
              options={[
                { value: "player", label: "Player" },
                { value: "staff", label: "Staff" },
              ]}
              value={formData.tenderFor}
              onChange={(value) => {
                const newFormData = { ...formData, tenderFor: value };
                // Reset tender type when switching between player and staff
                if (value === "staff") {
                  newFormData.staffTenderType = "full_time";
                  newFormData.tenderType = "buy"; // Reset to default
                } else {
                  newFormData.tenderType = "buy";
                  newFormData.staffTenderType = "full_time"; // Reset to default
                }
                setFormData(newFormData);
              }}
              placeholder="Select Player or Staff"
              displayProperty="label"
              valueProperty="value"
            />
          </div>

          <div>
            <CustomDropdown
              label="Request Type *"
              options={
                formData.tenderFor === "staff" ? staffTenderTypes : tenderTypes
              }
              value={
                formData.tenderFor === "staff"
                  ? formData.staffTenderType
                  : formData.tenderType
              }
              onChange={(value) => {
                if (formData.tenderFor === "staff") {
                  setFormData({ ...formData, staffTenderType: value });
                } else {
                  setFormData({ ...formData, tenderType: value });
                }
              }}
              placeholder="Request type"
              displayProperty="label"
              valueProperty="value"
            />
          </div>

          {formData.tenderFor === "player" && (
            <div>
              <CustomDropdown
                label="Recruiting for"
                options={recruitingForChoices}
                value={formData.recruitingFor}
                onChange={(value) =>
                  setFormData({ ...formData, recruitingFor: value })
                }
                placeholder="Select recruiting for"
                displayProperty="label"
                valueProperty="value"
              />
            </div>
          )}
        </div>

        {/* Conditional fields based on tender type and category */}

        {/* Buy tender type - Maximum Transfer fee */}
        {formData.tenderFor === "player" && formData.tenderType === "buy" && (
          <div>
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Maximum Transfer fee
            </label>
            <div className="relative">
              <input
                type="text"
                value={formatAmount(formData.maxTransferFee)}
                onChange={(e) => handleAmountChange(e, "maxTransferFee")}
                className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter amount"
                style={{
                  color: formData.maxTransferFee ? "#000000" : "#00000099",
                }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                {formData.currency || "USD"}
              </span>
            </div>
          </div>
        )}

        {/* Loan tender type - Loan Fee and Option to Buy */}
        {formData.tenderFor === "player" && formData.tenderType === "loan" && (
          <>
            {/* Two 50% columns in a non-wrapping row leave 150px each at 360,
                which is less than "Without Loan Fee" and less than "Is the loan
                with an option to buy?" — the two overprinted each other. The
                pattern is the one 473d031 ("make create tender page mobile
                responsive") already established for the salary row below; these
                two rows were simply missed by it. */}
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-[50%] w-full">
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Loan Fee
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.loanFee}
                      onChange={() =>
                        setFormData({ ...formData, loanFee: true })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Loan Fee
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.loanFee}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          loanFee: false,
                          loanFeeAmount: "",
                        })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Without Loan Fee
                    </span>
                  </label>
                </div>
              </div>

              <div className="lg:w-[50%] w-full">
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Is the loan with an option to buy?
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.loanWithOptionToBuy}
                      onChange={() =>
                        setFormData({ ...formData, loanWithOptionToBuy: true })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Yes
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.loanWithOptionToBuy}
                      onChange={() =>
                        setFormData({ ...formData, loanWithOptionToBuy: false })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      No
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Loan Fee Amount Input - Only show when loan fee is selected */}
            {formData.loanFee && (
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Max Loan Fee *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatAmount(formData.loanFeeAmount)}
                    onChange={(e) => handleAmountChange(e, "loanFeeAmount", 10)}
                    maxLength="10"
                    className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                    placeholder="Enter loan fee amount (max 10 digits)"
                    style={{
                      color: formData.loanFeeAmount ? "#000000" : "#00000099",
                    }}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                    {formData.currency || "USD"}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Trial tender type - Trial Period */}
        {formData.tenderFor === "player" && formData.tenderType === "trial" && (
          <div>
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Select trial period
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <CustomDateTimePicker
                  value={
                    formData.trialPeriod.start
                      ? new Date(formData.trialPeriod.start)
                      : null
                  }
                  onChange={(date) => {
                    const formattedDate = date
                      ? date.toISOString().split("T")[0]
                      : "";
                    setFormData({
                      ...formData,
                      trialPeriod: {
                        ...formData.trialPeriod,
                        start: formattedDate,
                      },
                    });
                  }}
                  placeholder="Start date"
                  className="flex-1 px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                />
              </div>
              <span className="text-[16px] font-medium text-[#000000]">to</span>
              <div className="flex-1">
                <CustomDateTimePicker
                  value={
                    formData.trialPeriod.end
                      ? new Date(formData.trialPeriod.end)
                      : null
                  }
                  onChange={(date) => {
                    const formattedDate = date
                      ? date.toISOString().split("T")[0]
                      : "";
                    setFormData({
                      ...formData,
                      trialPeriod: {
                        ...formData.trialPeriod,
                        end: formattedDate,
                      },
                    });
                  }}
                  placeholder="End date"
                  className="flex-1 px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                />
              </div>
            </div>
          </div>
        )}

        {/* Staff tender type - Duration in months */}
        {formData.tenderFor === "staff" &&
          formData.staffTenderType === "contract" && (
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Duration in months
              </label>
              <input
                type="text"
                value={formData.durationInMonths}
                onChange={(e) =>
                  setFormData({ ...formData, durationInMonths: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter duration"
                style={{
                  color: formData.durationInMonths ? "#000000" : "#00000099",
                }}
              />
            </div>
          )}

        {/* Player-specific fields - Only EU player (but NOT for trial type).
            Same missed row as the loan pair above: "Only Free players" wrapped
            to two lines while "Only EU player" stayed on one, so the two Yes/No
            pairs sat on different baselines. */}
        {formData.tenderFor === "player" && formData.tenderType !== "trial" && (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-[50%] w-full">
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Only EU player
              </label>
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.onlyEuPlayer}
                    onChange={() =>
                      setFormData({ ...formData, onlyEuPlayer: true })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    Yes
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!formData.onlyEuPlayer}
                    onChange={() =>
                      setFormData({ ...formData, onlyEuPlayer: false })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    No
                  </span>
                </label>
              </div>
            </div>

            <div className="lg:w-[50%] w-full">
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Only Free players
              </label>
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.onlyFreePlayers}
                    onChange={() =>
                      setFormData({ ...formData, onlyFreePlayers: true })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    Yes
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!formData.onlyFreePlayers}
                    onChange={() =>
                      setFormData({ ...formData, onlyFreePlayers: false })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    No
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Player + Trial specific - Only EU player (separate from other player types) */}
        {formData.tenderFor === "player" && formData.tenderType === "trial" && (
          <div>
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Only EU player
            </label>
            <div className="flex gap-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.onlyEuPlayer}
                  onChange={() =>
                    setFormData({ ...formData, onlyEuPlayer: true })
                  }
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  Yes
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!formData.onlyEuPlayer}
                  onChange={() =>
                    setFormData({ ...formData, onlyEuPlayer: false })
                  }
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  No
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Salary Type */}
          <div className="lg:w-[50%] w-full">
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Salary Type
            </label>
            <div className="flex gap-6">
              {salaryTypes.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.salaryType === option.value}
                    onChange={() =>
                      setFormData({ ...formData, salaryType: option.value })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Salary Calculation Type */}
          <div className="lg:w-[50%] w-full">
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Salary Calculation
            </label>
            <div className="flex gap-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.salaryCalculationType === "gross"}
                  onChange={() =>
                    setFormData({ ...formData, salaryCalculationType: "gross" })
                  }
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  Gross Salary
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.salaryCalculationType === "net"}
                  onChange={() =>
                    setFormData({ ...formData, salaryCalculationType: "net" })
                  }
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  Net Salary
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Salary Amount */}
        {formData.salaryType === "fixed_amount" ? (
          <div>
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              {formData.salaryCalculationType === "gross" ? "Gross" : "Net"}{" "}
              Salary per month
            </label>
            <div className="relative">
              <input
                type="text"
                value={formatAmount(formData.netSalary)}
                onChange={(e) => handleAmountChange(e, "netSalary", 10)}
                maxLength="10"
                className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter amount (max 10 digits)"
                style={{ color: formData.netSalary ? "#000000" : "#00000099" }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                {formData.currency || "USD"}
              </span>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Minimum Monthly{" "}
                {formData.salaryCalculationType === "gross" ? "Gross" : "Net"}{" "}
                Salary
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatAmount(formData.salaryRange.min)}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, "");
                    // Limit to 10 digits maximum to comply with API validation
                    if (value.length > 10) {
                      value = value.substring(0, 10);
                    }
                    setFormData({
                      ...formData,
                      salaryRange: {
                        ...formData.salaryRange,
                        min: value,
                      },
                    });
                  }}
                  className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                  placeholder="Min amount (max 10 digits)"
                  maxLength="10"
                  style={{
                    color: formData.salaryRange.min ? "#000000" : "#00000099",
                  }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                  {formData.currency || "USD"}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Maximum Monthly{" "}
                {formData.salaryCalculationType === "gross" ? "Gross" : "Net"}{" "}
                Salary
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatAmount(formData.salaryRange.max)}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, "");
                    // Limit to 10 digits maximum to comply with API validation
                    if (value.length > 10) {
                      value = value.substring(0, 10);
                    }
                    setFormData({
                      ...formData,
                      salaryRange: {
                        ...formData.salaryRange,
                        max: value,
                      },
                    });
                  }}
                  className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                  placeholder="Max amount (max 10 digits)"
                  maxLength="10"
                  style={{
                    color: formData.salaryRange.max ? "#000000" : "#00000099",
                  }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                  {formData.currency || "USD"}
                </span>
              </div>
            </div>
          </div>
        )}
        {fieldErrors.salary && (
          <p className="mt-1 text-sm text-red-500">{fieldErrors.salary}</p>
        )}

        {/* Bonus */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-[16px] font-medium text-[#000000] mb-2">
              Bonus *
            </label>
            <div className="flex gap-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.hasBonus}
                  onChange={() => setFormData({ ...formData, hasBonus: true })}
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  Yes
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!formData.hasBonus}
                  onChange={() => setFormData({ ...formData, hasBonus: false })}
                  className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                />
                <span className="ml-2 text-[16px] font-medium text-[#000000]">
                  No
                </span>
              </label>
            </div>
          </div>

          <>
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Travel
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.travel}
                      onChange={() =>
                        setFormData({ ...formData, travel: true })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Yes
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.travel}
                      onChange={() =>
                        setFormData({ ...formData, travel: false })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      No
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Housing
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.housing}
                      onChange={() =>
                        setFormData({ ...formData, housing: true })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Yes
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.housing}
                      onChange={() =>
                        setFormData({ ...formData, housing: false })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      No
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Food covered
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.foodCovered}
                      onChange={() =>
                        setFormData({ ...formData, foodCovered: true })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      Yes
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!formData.foodCovered}
                      onChange={() =>
                        setFormData({ ...formData, foodCovered: false })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      No
                    </span>
                  </label>
                </div>
              </div>
            </>
        </div>
      </div>
    </div>
  );
};

export default TenderFormDetails;
